/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface BackfillPayoutHandlesButtonProps {
  isAdmin: boolean;
}

interface BackfillResult {
  success: boolean;
  totalPayouts: number;
  updatedCount: number;
  alreadyCompleteCount: number;
  unmatchedHandleCount: number;
  unmatchedOrderNumberCount: number;
  unmatchedHandle: { id: string; collectionTitle: string | null }[];
  unmatchedOrderNumber: { id: string; orderCount: number }[];
}

export default function BackfillPayoutHandlesButton({ isAdmin }: BackfillPayoutHandlesButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBackfill = async () => {
    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/backfill-payout-collection-handles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
      } else {
        setError(data.error || 'Backfill failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div>
      <button
        onClick={handleBackfill}
        disabled={isLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Backfilling...' : 'Backfill Payout Metadata'}
      </button>

      {isLoading && (
        <div className="mt-4 text-sm text-gray-600">
          <p>⏳ Stamping collectionHandle and lastOrderNumberPaid onto older payout records...</p>
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-green-800 font-semibold">✅ Backfill Completed</p>
          <p className="text-sm text-green-700 mt-1">
            {result.updatedCount} updated, {result.alreadyCompleteCount} already complete,
            {' '}{result.unmatchedHandleCount} missing a handle match,
            {' '}{result.unmatchedOrderNumberCount} missing a valid order number
            {' '}(out of {result.totalPayouts} total payouts)
          </p>
          {result.unmatchedHandleCount > 0 && (
            <div className="mt-2 text-xs text-amber-700">
              <p>Unmatched collectionTitle values (no matching handle found in shopify_metadata/collections):</p>
              <ul className="list-disc ml-4">
                {result.unmatchedHandle.map((u) => (
                  <li key={u.id}>{u.id}: {u.collectionTitle || '(no collectionTitle either)'}</li>
                ))}
              </ul>
            </div>
          )}
          {result.unmatchedOrderNumberCount > 0 && (
            <div className="mt-2 text-xs text-amber-700">
              <p>Payouts with no valid numeric order number (couldn't compute lastOrderNumberPaid):</p>
              <ul className="list-disc ml-4">
                {result.unmatchedOrderNumber.map((u) => (
                  <li key={u.id}>{u.id}: {u.orderCount} order(s) on record</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-800 font-semibold">❌ Backfill Failed</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}
    </div>
  );
}
